// WorkNotes Service for backend communication
import { api } from './api';

export type NoteType = 'General' | 'StatusChange' | 'Milestone' | 'Issue' | 'Idea';

export interface WorkNoteDto {
  id: string;
  projectId: string;
  projectTitle?: string;
  userId: string;
  authorName?: string;
  content: string;
  stageAtCreation: string;
  noteType: NoteType;
  isPinned: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface NoteHistoryDto {
  stage: string;
  stageDisplayName: string;
  notes: WorkNoteDto[];
  noteCount: number;
}

export interface CreateNoteRequest {
  content: string;
  noteType?: NoteType;
  isPinned?: boolean;
}

export interface UpdateNoteRequest {
  content: string;
  noteType?: NoteType;
}

// WorkNotes API
export const workNotesApi = {
  // Get all notes for a project
  getNotes: (projectId: string, pinnedOnly = false): Promise<WorkNoteDto[]> => {
    const params = pinnedOnly ? '?pinnedOnly=true' : '';
    return api.get<WorkNoteDto[]>(`/projects/${projectId}/notes${params}`);
  },

  // Get a specific note by ID
  getNoteById: (projectId: string, noteId: string): Promise<WorkNoteDto> => {
    return api.get<WorkNoteDto>(`/projects/${projectId}/notes/${noteId}`);
  },

  // Get notes for a specific stage
  getNotesByStage: (projectId: string, stage: string): Promise<WorkNoteDto[]> => {
    return api.get<WorkNoteDto[]>(`/projects/${projectId}/notes/stage/${encodeURIComponent(stage)}`);
  },

  // Get note history (timeline grouped by stage)
  getNoteHistory: (projectId: string): Promise<NoteHistoryDto[]> => {
    return api.get<NoteHistoryDto[]>(`/projects/${projectId}/notes/history`);
  },

  // Create a new note
  createNote: (projectId: string, data: CreateNoteRequest): Promise<WorkNoteDto> => {
    return api.post<WorkNoteDto>(`/projects/${projectId}/notes`, data);
  },

  // Update an existing note
  updateNote: (projectId: string, noteId: string, data: UpdateNoteRequest): Promise<WorkNoteDto> => {
    return api.put<WorkNoteDto>(`/projects/${projectId}/notes/${noteId}`, data);
  },

  // Toggle pin status of a note
  togglePin: (projectId: string, noteId: string): Promise<WorkNoteDto> => {
    return api.post<WorkNoteDto>(`/projects/${projectId}/notes/${noteId}/toggle-pin`);
  },

  // Delete a note (soft delete)
  deleteNote: (projectId: string, noteId: string): Promise<void> => {
    return api.delete(`/projects/${projectId}/notes/${noteId}`);
  },
};

export default workNotesApi;
